import { useMutation } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import { FlagsmithExportSchemaType } from "@reflag/shared/importAPI";

export type FlagsmithExportDownload = {
  blob: Blob;
  filename: string;
};

function getDownloadFilename(contentDisposition?: string): string {
  const filename = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1];
  return filename ?? "flagsmith-export.json";
}

export function useFlagsmithExportMutation() {
  const { app } = useEnvironment();

  return useMutation<
    FlagsmithExportDownload,
    AxiosError<ErrorResponse>,
    FlagsmithExportSchemaType
  >({
    mutationFn: async (data) => {
      try {
        const response = await api.post<"/apps/:appId/import/flagsmith/export">(
          `/apps/${app.id}/import/flagsmith/export`,
          data,
          {
            responseType: "blob",
          },
        );

        return {
          blob: response.data as unknown as Blob,
          filename: getDownloadFilename(
            response.headers["content-disposition"],
          ),
        };
      } catch (error) {
        if (isAxiosError(error) && error.response?.data instanceof Blob) {
          try {
            error.response.data = JSON.parse(await error.response.data.text());
          } catch {
            // Keep the original response when it is not a JSON error.
          }
        }
        throw error;
      }
    },
  });
}
